/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '.';
import { getApi } from '../common/requestHelper';
import { FeedbackApi } from '@ink-ai/insight-service-sdk';

export enum FEEDBACK_TYPE {
  FEATURE_REQUEST = 'FEATURE_REQUEST',
  BUG_REPORT = 'BUG_REPORT',
}

const initialState = {
  feedbackContent: '',
  feedbackType: FEEDBACK_TYPE.FEATURE_REQUEST,
  loading: false,
  visible: false,
};

export type FeedbackState = typeof initialState;

export const sendFeedback = createAsyncThunk(
  'feedback/SendFeedback',
  async (_, { dispatch, getState }) => {
    dispatch(feedback.actions.startLoading());
    const state = getState() as RootState;
    const feedbackApi = await getApi(FeedbackApi);

    const result = await feedbackApi.createFeedback({
      feedbackContent: state.feedback.feedbackContent,
      feedbackType: state.feedback.feedbackType,
      userId: state.auth.userId,
    });
    return result;
  },
);

export const feedback = createSlice({
  name: 'feedback',
  initialState: initialState,
  reducers: {
    updateFeedbackType: (state, { payload }: PayloadAction<FEEDBACK_TYPE>) => {
      state.feedbackType = payload;
    },
    updateFeedbackContent: (state, { payload }: PayloadAction<string>) => {
      state.feedbackContent = payload;
    },
    setVisibility: (state, { payload }: PayloadAction<boolean>) => {
      state.visible = payload;
    },
    startLoading: (state) => {
      state.loading = true;
    },
    clearAll: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendFeedback.fulfilled, () => {
      return initialState;
    });
    builder.addCase(sendFeedback.rejected, (_state, action) => {
      console.error(action.error);
      return initialState;
    });
  },
});
